<template>
  <div v-if="isMotorCockpitEnabled">
    <Card v-if="loading" :loadingState="true"></Card>
    <div v-if="!loading">
      <SelfServiceTile
        :changeRequestType="changeRequestType"
        :checkPoliciesDetails="checkPoliciesDetails"
        :checkPolicyDetails="checkPolicyDetails"
        :checkPolicyOverview="checkPolicyOverview"
        :formAbortAction="formAbortAction"
        :formLoading="formLoading"
        :formShowResponse="formShowResponse"
        :formSubmitDisabled="formSubmitDisabled"
        :formSubmitting="formSubmitting"
        :getFormBlocks="getFormBlocks"
        :getListBlocks="getListBlocks"
        :modalCurrentPremium="modalCurrentPremium"
        :modifyPolicy="modifyPolicy"
        :multiSelect="false"
        :preSelectedPolicyIds="preSelectedPolicyIds"
        :selfServiceLabels="selfServiceLabels!"
        :setSelectedPolicies="setSelectedPolicies"
        :showDateOption="false"
        :staticPoliciesWithIconAndTitles="staticPoliciesFiltered()"
        @formSubmit="formSubmit"
      >
        <template v-slot:change-component>
          <MotorCockpitForm
            ref="form"
            :changeType="changeType"
            :isProlongationContract="isProlongationCaseTwo()"
            :modal="modal"
            :motorDetails="selectedMotorDetails!"
            :partnerProxyId="partnerProxyId"
            :premiumSubtext="premiumSubtext"
            @formShowResponse="setFormShowResponse"
            @formSubmitting="setFormSubmitting"
            @formsValid="setFormSubmitEnabled"
          />
        </template>
        <template v-slot:list-extra>
          <div class="mt-16">
            <div class="text">
              Möchten Sie an einem anderen Vertrag eine Anpassung vornehmen?<br />
              Bitte nutzen Sie hierfür unser <a :href="formularUrl">Formular</a>.
            </div>
          </div>
        </template>
      </SelfServiceTile>
    </div>
  </div>
  <Card
    v-else
    title="Technisches Update: Service temporär nicht verfügbar"
    :backgroundColorCard="true"
  >
    <template v-slot:content>
      <p>
        Aktuell steht dieser Service leider nicht zur Verfügung. Bitte wenden Sie sich an
        unseren Kundenservice unter
        <a href="tel:+496971158500">069&nbsp;7115&nbsp;8500</a>. Wir sind rund um die Uhr
        für Sie erreichbar und helfen Ihnen gerne weiter.<br /><br />Vielen Dank für Ihr
        Verständnis.
      </p>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import {
  Card,
  ChangeRequestType,
  type KeyValueType,
  type PolicyPaymentData,
  type PolicyWithIconAndTitles,
  type SelfServiceLabels,
  SelfServiceTile,
  type TitleTextType
} from 'de-zurich-ui-library'
import { onBeforeMount, onMounted, ref, type Ref } from 'vue'

import { useI18n } from 'vue-i18n'
import MotorCockpitForm from '@/componentsTenant/motor/cockpit/MotorCockpitForm.vue'
import { partnerProxyIdReg } from '@/utilsTenant/PropsValidation'
import { type PolicyMotorDetails } from '@/types/motor/PolicyMotorDetails'
import { getPartnerProxyId } from '@/utils/KeycloakUtils'
import { navigate } from '@/utils/NavigationUtils'
import { ChangeFormEnum } from '@/typesTenant/MotorContractChange'
import {
  isVersicherungsumfangChangeAllowed,
  printPremium
} from '@/utilsTenant/MotorUtils'
import { sanitizeUrl } from '@braintree/sanitize-url'
import { isTrue } from '@/utils/CommonUtils'
import { usePoliciesStore } from '@/stores/policiesStore'
import { useMotorStore } from '@/stores/motorStore'
import { type MotorContract } from '@/types/motor/MotorContract'
import { fetchMotorContractsForSelfService } from '@/stores/motorService'
import {
  resolveMotorIconString,
  resolveMotorTitle,
  swapLettersAndNumbers
} from '@/utils/motor/MotorUtilsCommon'

const { t } = useI18n()

const policiesStore = usePoliciesStore()

const isMotorCockpitEnabled = ref(
  isTrue(globalThis.ENV_CONFIG?.FEATURE_FLAGS.MOTOR.ENABLE_MOTOR_COCKPIT) ?? false
)

const props = defineProps({
  partnerProxyId: {
    type: String,
    required: true,
    validator(value: string): boolean {
      return partnerProxyIdReg.test(value)
    }
  },
  changeType: {
    type: String,
    required: true,
    default: ''
  }
})
const form = ref<typeof MotorCockpitForm | null>(null)
const formularUrl: string = sanitizeUrl(globalThis.ENV_CONFIG?.REDIRECTS.CONTRACT_FORM)
const partnerProxyIdValue = ref()
const policies: Ref<PolicyWithIconAndTitles[]> = ref([])
const policiesMotorDetails: Ref<PolicyMotorDetails[]> = ref([])
const preSelectedPolicyIds: Ref<string[]> = ref([])
const selectedMotorDetails: Ref<PolicyMotorDetails | undefined> = ref()

// SelfServiceForm
const changeRequestType: Ref<ChangeRequestType> = ref(ChangeRequestType.MotorCockpit)
const selfServiceLabels: Ref<SelfServiceLabels | undefined> = ref()
const formShowResponse = ref(false)
const formSubmitDisabled = ref(true)
const formLoading = ref(false)
const formSubmitting = ref(false)
const loading = ref(true)

// Prolongation
const isProlongation = ref(false)
const contractValidFrom = ref(new Date())
const premiumSubtext = ref('')
const modal: Record<string, string> = {
  title: 'Hinweis zum Beitrag',
  text: 'Hier sehen Sie Ihren zukünftigen Beitrag ab der kommenden Hauptfälligkeit. Für den aktuellen Zeitraum bis zur nächsten Hauptfälligkeit erhalten Sie nach Abschluss Ihres Änderungswunsches zusätzlich einen Nachtrag bis zur nächsten Hauptfälligkeit.'
}

const modalCurrentPremium: TitleTextType = {
  title: 'Hinweis zum Beitrag',
  text: 'Hier sehen Sie Ihren zukünftigen Beitrag ab der kommenden Hauptfälligkeit.'
}

onBeforeMount(() => {
  partnerProxyIdValue.value = props.partnerProxyId
  loadSelfServiceLabels()
})

onMounted(async () => {
  // Preselection: consume all policy ids once for the cockpit
  preSelectedPolicyIds.value = policiesStore.consumePreSelectedPolicyIds()

  const ppId = getPartnerProxyId()

  if (!ppId) {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)
    throw new Error('No partner proxy id available!')
  } else if (!props.partnerProxyId && ppId) {
    partnerProxyIdValue.value = ppId
  }

  try {
    policiesMotorDetails.value = await fetchMotorContractsForSelfService(ppId)

    policiesMotorDetails.value = policiesMotorDetails.value.filter(
      (policyMotorDetails: PolicyMotorDetails) => policyMotorDetails.isValid
    )

    if (props.changeType === ChangeFormEnum.ChangeVersicherungsumfang) {
      policiesMotorDetails.value = policiesMotorDetails.value.filter(
        (policyMotorDetails: PolicyMotorDetails) =>
          isVersicherungsumfangChangeAllowed(policyMotorDetails)
      )
    }

    if (!policiesMotorDetails.value || policiesMotorDetails.value.length === 0) {
      emptyPolicies()
    } else {
      policies.value =
        await enrichDisabledPoliciesWithPendingStatusInformation(mapPolicies())
    }
  } catch (error) {
    if (error instanceof Error && error.message === 'Unauthorized') {
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)
    }
    emptyPolicies()
  } finally {
    loading.value = false
  }
})

function staticPoliciesFiltered(): PolicyWithIconAndTitles[] {
  return policies.value.filter(
    (policy: PolicyWithIconAndTitles) => policy.policyStatus == 'ACTIVE'
  )
}

// 31 - READY_FOR_ISSUE - Schwebe bereit zur Ausfertigung
// (neue Änderung kann nach Ausfertigung in Host dunkelverarbeitet werden)
// 31 Text: Für diesen Vertrag wird aktuell bereits eine Änderung verarbeitet. Weitere Änderungen können Sie voraussichtlich ab dem nächsten Werktag wieder vornehmen.
// --
// 32 - ON_HOLD - Schwebe im Haltezustand
// (neue Änderung ist nur möglich, wenn Haltezustand manuell von ZKC&ZKS rausgenommen werden)
// -> Keine Änderung in Cockpit zulassen (d.h. Keine Preisberechnung und kein Abschluss)
// 32 Text: Ihr Vertrag befindet sich aktuell in Bearbeitung. Daher sind derzeit keine Änderungen möglich.
// --
// 33 - AWAITING_PROCESSING - Schwebe im Wartezustand
// (neue Änderung ist nur möglich, wenn Wartezustand verarbeitet wurde)
// -> Keine Änderung in Cockpit zulassen  (d.h. Keine Preisberechnung und kein Abschluss)
// 33 Text: Für diesen Vertrag wird aktuell bereits eine Änderung verarbeitet. Weitere Änderungen können Sie ab dem tt.mm.jjjj wieder vornehmen.
// Datum soll von Backend kommen
// --
// 35 - PENDING_CANCELLATION - Halte bis 29a3 bzw. 29c kommt
// (neue Änderung auf vorgemerkten Stornierung des Vertrags nicht zulässig)
// -> Keine Änderung in Cockpit zulassen (d.h. Keine Preisberechnung und kein Abschluss)
// 35 Text: Ihr Vertrag wird aktuell storniert bzw. beendet. Daher können derzeit keine Änderungen vorgenommen werden.
const PendingStatusRecord: Record<
  string,
  string | ((contractValidFromDate: Date | null) => string)
> = {
  READY_FOR_ISSUE:
    'Für diesen Vertrag wird aktuell bereits eine Änderung verarbeitet. Weitere Änderungen können Sie voraussichtlich ab dem nächsten Werktag wieder vornehmen.',
  ON_HOLD:
    'Ihr Vertrag befindet sich aktuell in Bearbeitung. Daher sind derzeit keine Änderungen möglich.',
  AWAITING_PROCESSING: (contractValidFromDate) => {
    const date = contractValidFromDate?.toLocaleDateString('de-DE') ?? 'unbekannt'
    return `Für diesen Vertrag wird aktuell bereits eine Änderung verarbeitet. Weitere Änderungen können Sie ab dem ${date} wieder vornehmen.`
  },
  PENDING_CANCELLATION:
    'Ihr Vertrag wird aktuell storniert bzw. beendet. Daher können derzeit keine Änderungen vorgenommen werden.'
}

const DISABLED_REASON_TITLE = 'Anpassung leider nicht möglich'

async function enrichDisabledPoliciesWithPendingStatusInformation(
  policies: PolicyWithIconAndTitles[]
): Promise<PolicyWithIconAndTitles[]> {
  return Promise.all(
    policies.map(async (policy) => {
      if (!policy.disabled) return policy

      let contractValidFromDate: Date | null = null

      const pendingStatus = String(policy.pendingStatus ?? null)

      if (!pendingStatus) return policy

      if (pendingStatus === 'AWAITING_PROCESSING') {
        try {
          const motorContract = (await useMotorStore().getMotorContractByPolicyId(
            policy.policyId ?? ''
          )) as MotorContract

          contractValidFromDate = new Date(
            motorContract?.pendingProcess?.pendingDate ?? ''
          )
        } catch {
          contractValidFromDate = null
        }
      }
      const messageOfPendingStatus = PendingStatusRecord[pendingStatus]

      if (!messageOfPendingStatus) return policy

      const hintContent =
        typeof messageOfPendingStatus === 'function'
          ? messageOfPendingStatus(contractValidFromDate)
          : messageOfPendingStatus

      return {
        ...policy,
        hintTitle: DISABLED_REASON_TITLE,
        hintContent
      }
    })
  )
}

function loadSelfServiceLabels(): void {
  selfServiceLabels.value = {
    listHeadline: t('motorCockpit.selfServiceLabels.listHeadline'),
    formCurrentDataLabel: t('motorCockpit.selfServiceLabels.formCurrentDataLabel'),
    formCurrentDataValue: t('motorCockpit.selfServiceLabels.formCurrentDataValue'),
    formCurrentExtraText: '',
    formCardTitle: t('motorCockpit.selfServiceLabels.formCardTitle'),
    formDateLabel: t('motorCockpit.selfServiceLabels.formDateLabel'),
    formDateErrorText: t('motorCockpit.selfServiceLabels.formDateErrorText'),
    responseTitle: t('motorCockpit.selfServiceLabels.responseTitle'),
    responseText: isProlongationCaseTwo()
      ? t('motorCockpit.selfServiceLabels.responseTextProlongation')
      : t('motorCockpit.selfServiceLabels.responseText'),
    responseTileBackButtonLabel: t(
      'motorCockpit.selfServiceLabels.responseTileBackButtonLabel'
    ),
    errorTitle: t('motorCockpit.selfServiceLabels.errorTitle'),
    errorText: t('motorCockpit.selfServiceLabels.errorText')
  }
}

function isProlongationCaseTwo(): boolean {
  // Drei Bedingungen müssen erfüllt sein
  // 1: Prolongation liegt vor
  // 2: contractValidFrom > today
  // 3: Änderungsschlüssel, 50 <= vertragsStandSchluessel <= 79
  // Dann true

  if (!contractValidFrom.value) return false

  const today = new Date()

  const isInFuture = contractValidFrom.value > today

  return isProlongation.value && isInFuture
}

function mapPolicies() {
  let index = 0
  const policies: PolicyWithIconAndTitles[] = []
  for (const policyMotorDetails of policiesMotorDetails.value) {
    const policyNumber =
      policyMotorDetails.motorContract.contractBase.policyNumber.replace(/^[0|\D]*/, '')
    policiesMotorDetails.value[index]!.motorContract.contractBase.policyNumber =
      policyNumber
    index = index + 1

    const motorContract = policyMotorDetails.motorContract
    const licenceNumber = motorContract.insuredVehicle.licenceNumber
    const secondTitleBase = swapLettersAndNumbers(licenceNumber) ?? licenceNumber
    const secondTitle = motorContract.insuredVehicle?.seasonalPeriod
      ? `${secondTitleBase} / ${t('motorPolicyTile.seasonal')}`
      : secondTitleBase

    const policy: PolicyWithIconAndTitles = {
      beginningDatePolicy: '',
      ddsLines: ['Motor'],
      disabled: policyMotorDetails.motorContract.pendingProcess.pendingStatus != 'NONE',
      expiryDatePolicy: '',
      firstTitle: resolveMotorTitle(motorContract),
      iconIdentifier: resolveMotorIconString(motorContract),
      insuranceCompanyCode: policyMotorDetails.motorContract.contractBase.companyCode,
      modificationDatePolicy: '',
      partnerAddressIDVN: '',
      paymentData: policyMotorDetails.motorContract.paymentData as PolicyPaymentData,
      pendingStatus: policyMotorDetails.motorContract.pendingProcess.pendingStatus,
      policyId:
        policyMotorDetails.motorContract.contractBase.companyCode + '-' + policyNumber,
      policyNumber,
      policyLabel: licenceNumber,
      policyLines: ['motor'],
      policyStatus: 'ACTIVE',
      secondTitle,
      tarif: policyMotorDetails.motorContract.liabilityInsurance.tarif
    }
    policies.push(policy)
  }
  return policies
}

function emptyPolicies() {
  policies.value = []
}

function checkPolicyOverview(): boolean {
  return true
}

function modifyPolicy(policy: PolicyWithIconAndTitles) {
  return policy
}

function checkPoliciesDetails(): boolean {
  return true
}

function checkPolicyDetails(): boolean {
  return true
}

function setSelectedPolicies(policies: PolicyWithIconAndTitles[]): void {
  if (policies.length === 0) {
    selectedMotorDetails.value = undefined
    return
  }

  if (policies.length !== 1) {
    handleInvalidPolicyCount(policies.length)
    return
  }

  const foundPolicyMotorDetails = findPolicyMotorDetails(policies[0]!.policyNumber)
  if (!foundPolicyMotorDetails || !foundPolicyMotorDetails.motorContract.paymentData) {
    handlePolicyNotFound()
    return
  }

  updateSelectedPolicyDetails(foundPolicyMotorDetails)
}

function findPolicyMotorDetails(policyNumber: string): PolicyMotorDetails | undefined {
  return policiesMotorDetails.value.find(
    (policyMotorDetails) =>
      policyMotorDetails.motorContract.contractBase.policyNumber === policyNumber
  )
}

function handleInvalidPolicyCount(count: number): void {
  emptyPolicies()
  throw new Error(`Should get exactly 1 policy, but got ${count} policies.`)
}

function handlePolicyNotFound(): void {
  emptyPolicies()
  throw new Error('Selected policy not found in policyMotorDetails.')
}

function updateSelectedPolicyDetails(policyDetails: PolicyMotorDetails): void {
  selectedMotorDetails.value = policyDetails
  isProlongation.value = policyDetails.isProlongation
  contractValidFrom.value = new Date(
    policyDetails.motorContract?.prolongation?.contractValidFrom ?? ''
  )

  updatePremiumLabels(policyDetails)
}

function updatePremiumLabels(policyDetails: PolicyMotorDetails): void {
  if (!selfServiceLabels.value) {
    return
  }

  if (isProlongationCaseTwo()) {
    updateProlongationText()
  }

  updateFormCurrentExtraText(policyDetails)
}

function updateProlongationText(): void {
  premiumSubtext.value =
    '* Beitrag gültig ab: ' +
    new Date(contractValidFrom.value).toLocaleDateString('de-de')
  selfServiceLabels.value!.currentPremiumSubText = premiumSubtext.value
}

function updateFormCurrentExtraText(policyDetails: PolicyMotorDetails): void {
  const premiumAmount =
    policyDetails.motorContract.paymentData!.grossPremiumBasedOnPaymentPeriod?.amount ??
    -1

  if (isProlongationCaseTwo()) {
    selfServiceLabels.value!.formCurrentExtraText =
      t(`motorCockpit.common.premium`) + ' ' + printPremium(premiumAmount) + '*'
  } else {
    selfServiceLabels.value!.formCurrentExtraText =
      t(`motorCockpit.common.currentPremium`) + ' ' + printPremium(premiumAmount)
  }
}

function formAbortAction(): void {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.CONTRACT_SERVICE_PAGE)
}

function getListBlocks(): KeyValueType[] {
  return []
}

function getFormBlocks(policies: PolicyWithIconAndTitles[]): KeyValueType[][] {
  const blocks: KeyValueType[][] = []
  for (const policy of policies) {
    const block: KeyValueType[] = []
    // default policy blocks
    block.push({
      key: `${t('motorCockpit.common.vsnr')}: ${policy.policyNumber}`,
      value: `${t('motorCockpit.common.tarif')}: ${
        policy.tarif ? t('motorCockpit.tarif.' + policy.tarif) : ''
      }`,
      type: 'policy'
    })
    // individual blocks

    block.push({
      key: `${t('motorCockpit.common.kennzeichen')}: ${policy.secondTitle}`,
      value: `${t('motorCockpit.common.zahlweise')}: ${
        policy && policy.paymentData
          ? t(`paymentPeriod.${policy.paymentData.paymentPeriod}`)
          : t(`paymentPeriod.UNKNOWN`)
      }`,
      type: 'info'
    })
    blocks.push(block)
  }
  return blocks
}

function setFormSubmitting(value: boolean): void {
  formSubmitting.value = value
}

function setFormSubmitEnabled(value: boolean): void {
  formSubmitDisabled.value = !value
}

function formSubmit(): void {
  formSubmitting.value = true
  form.value?.formSubmit()
}

function setFormShowResponse(value: boolean, success: boolean): void {
  if (!success && selfServiceLabels.value) {
    selfServiceLabels.value.responseTitle = t(
      'motorCockpit.selfServiceLabels.responseTitleError'
    )
    selfServiceLabels.value.responseText = t(
      'motorCockpit.selfServiceLabels.responseTextError'
    )
    formShowResponse.value = value
  }
  if (success) navigate('/danke?vertrag-aendern')
}
</script>

<style lang="scss" scoped>
a {
  color: var(--link-color);
  text-decoration-line: none;
}

a:hover {
  text-decoration-line: underline;
}

.text {
  font-family: var(--f-primary-light), serif;
  font-size: var(--base-font-size);
  margin-bottom: var(--baseline);
}

:deep(*[class^='extrabox--']) {
  background-color: #ffffff !important;
}

:deep(.btnRecalculate:not(.button--cta3--disabled)) {
  background-color: #0071c8 !important;
  border-color: #0071c8 !important;
}

:deep(.btnRecalculate:not(.button--cta3--disabled)):hover {
  background-color: #005fa3 !important;
  border-color: #005fa3 !important;
}

:deep(.button--cta1) {
  background-color: #dc3704 !important;
}

:deep(.button--cta1:hover) {
  background-color: #cc431c !important;
}
</style>
